import { ERRORS } from "../../../../constants/errors";
import type { Answers, Groups } from "../../../../features/questionnaire";
import type { Answer } from "../../Section/Question/types";
import { getErrorMessage, ok } from "../question/rules";
import { validateQuestion } from "../../../validation/question/validate";
import { getCompareFunction } from "./compare";
import { evaluateExpression } from "./expressionEvaluator";
import type {
  Combinator,
  EvaluateRule,
  PartialQuestion,
  Rule,
  RuleSet,
  VisibilityMap,
} from "./types";
import { isRule, isRuleSet } from "./types";
import { shouldValidateNonVisible } from "./shouldValidateNonVisible";

export type ValidateQuestionWithFormRulesParams = {
  question: PartialQuestion;
  answers: Answers;
  visibilityMap: VisibilityMap;
  locale?: string;
  groups?: Groups;
};

// A rule's field can be in one of three visibility states:
// 1. Visible on current page:  visibilityMap[id] === true   → evaluate normally
// 2. Explicitly hidden:        visibilityMap[id] === false   → skip, treat as valid
// 3. On another page:          !(id in visibilityMap)        → evaluate if answer exists, skip otherwise
const shouldSkipRule = (
  ruleId: string,
  answers: Answers,
  visibilityMap: VisibilityMap = {},
): boolean => {
  const isExplicitlyHidden = visibilityMap[ruleId] === false;
  const isOnAnotherPageWithoutAnswer =
    !(ruleId in visibilityMap) && answers[ruleId] == null;

  return isExplicitlyHidden || isOnAnotherPageWithoutAnswer;
};

// Pre-generate rule evaluation functions
const compileRule = (rule: Rule): EvaluateRule => {
  const evaluateRule = getCompareFunction(rule.operator);

  return (answers: Answers, visibilityMap: VisibilityMap = {}) => {
    if (shouldSkipRule(rule.id, answers, visibilityMap)) {
      return { id: rule.id, valid: true, message: undefined };
    }

    const compareValue = rule.expression
      ? evaluateExpression(rule.expression, {
          answers,
          visibilityMap,
          evaluatedAnswers: rule.evaluatedAnswers,
        })
      : rule.value;

    const leftHandValue =
      rule.leftValue !== undefined
        ? rule.leftValue
        : rule.leftExpression
          ? evaluateExpression(rule.leftExpression, {
              answers,
              visibilityMap,
              evaluatedAnswers: rule.evaluatedAnswers,
            })
          : answers[rule.id];

    const valid =
      leftHandValue == null || compareValue == null
        ? false
        : evaluateRule(leftHandValue, compareValue);

    return {
      id: rule.id,
      valid,
      message: valid && isRuleSet(rule) ? undefined : (rule as RuleSet).message,
    };
  };
};

const compileCombinator = (combinator: Combinator) => {
  const compiledRules: EvaluateRule[] = combinator.rules.map(
    ruleOrCombinator => {
      if (isRule(ruleOrCombinator)) {
        return compileRule(ruleOrCombinator);
      }
      return compileCombinator(ruleOrCombinator);
    },
  );

  return (answers: Answers, visibilityMap: VisibilityMap = {}) => {
    const results = compiledRules.map(rule => rule(answers, visibilityMap));

    // Exclude explicitly hidden fields; cross-page fields (not in visibilityMap) are kept as applicable
    const applicableResults = results.filter(
      ({ id }) => visibilityMap[id] !== false,
    );

    // Defer validation until all applicable fields have answers
    if (combinator.skipIfAnyUnanswered) {
      const hasUnansweredApplicableRule = combinator.rules.some(
        (rule, i) =>
          visibilityMap[results[i].id] !== false &&
          isRule(rule) &&
          answers[results[i].id] == null,
      );

      if (hasUnansweredApplicableRule) {
        return { id: combinator.id, valid: true, message: undefined };
      }
    }

    if (applicableResults.length === 0) {
      return {
        id: combinator.id,
        valid: true,
        message: undefined,
      };
    }

    const valid =
      combinator.combinator === "and"
        ? applicableResults.every(result => result.valid)
        : applicableResults.some(result => result.valid);

    return {
      id: combinator.id,
      valid,
      message:
        valid && isRuleSet(combinator)
          ? undefined
          : (combinator as RuleSet).message,
    };
  };
};

// Pre-generate rule set (combination) evaluation functions
const compileRuleSet = (ruleSet: RuleSet) => {
  return isRule(ruleSet) ? compileRule(ruleSet) : compileCombinator(ruleSet);
};

const isRequiredButEmpty = (
  question: PartialQuestion,
  answer?: Answer,
): boolean => {
  if (!question.visible && !question.suggested) return false;
  if (!(question.required || question.isRequired)) return false;

  return !answer || (Array.isArray(answer) && !answer.length);
};

export const isOptionalAndEmpty = (
  question: PartialQuestion,
  answer?: Answer,
): boolean => {
  if (question.required || question.isRequired) return false;

  return !answer || (Array.isArray(answer) && !answer.length);
};

// Factory function to create a validator from rules data
export const createValidator = (validations: readonly RuleSet[]) => {
  const compiledRuleset = validations.map(compileRuleSet);

  return ({
    question,
    answers,
    visibilityMap,
    locale,
    groups,
  }: ValidateQuestionWithFormRulesParams) => {
    const answer = answers[question.name];

    // Skip validation if question is not visible, or if visibilityMap indicates hidden
    // (with exception for non-visible questions that should still validate)
    if (
      (!question.visible && !question.suggested) ||
      (visibilityMap?.[question.name] === false &&
        !shouldValidateNonVisible({
          question,
          groups,
          answers,
          visibilityMap,
        }))
    ) {
      return ok(question.name);
    }

    if (isRequiredButEmpty(question, answer)) {
      return {
        id: question.name,
        valid: false,
        message: getErrorMessage({
          questionErrorMessage: question.errorMessage,
          fallbackMessage: ERRORS.REQUIRED,
        }),
      };
    }

    // Percent group shadow questions derive their answer from individual group
    // questions (via buildPercentagesAnswerForGroup), so the direct answer
    // check in isOptionalAndEmpty would incorrectly skip validation.
    const derivesAnswerFromGroup =
      question.validation?.type === "percentages" &&
      question.display?.type === "percentage_split_group";

    if (derivesAnswerFromGroup || !isOptionalAndEmpty(question, answer)) {
      const localValidation = validateQuestion({
        question,
        answers,
        locale,
        groups,
      });

      if (!localValidation.valid) {
        return localValidation;
      }
    }

    // Find the first form-level rule that fails
    const formValidation = compiledRuleset
      .map(ruleFunction => ruleFunction(answers, visibilityMap))
      .filter(
        result => result.id === question.name && result.valid === false,
      )[0];

    return formValidation ?? ok(question.name);
  };
};
